import { CampaignSettingsProps } from "../components/campaign/types";

// MB-1455: the account-wide Instagram weekly limit, self-exclusion aware.
//
// The rule is 3 Instagram posts per week across every campaign on the account.
// Days from OTHER campaigns always cost, even when they fall on a day this
// campaign also posts - two campaigns on the same Tuesday is two posts, not
// one. The only thing that must not double-count is the campaign being edited
// against itself, and excludeCampaign handles that upstream by keeping the
// edited campaign out of the count entirely.

export const INSTAGRAM_WEEKLY_POST_LIMIT = 3;

export interface InstagramLimitResult {
    allowed: boolean;
    /** Weekly Instagram posts used by OTHER campaigns (edited one excluded). */
    otherPosts: number;
    /** Those plus what this campaign's selection would schedule. */
    totalPosts: number;
    /** How many this campaign may still schedule. */
    remaining: number;
    limit: number;
}

export const checkInstagramWeeklyLimit = (
    otherPosts: number,
    selectedDays: string[] | undefined,
    postsPerDay = 1,
    limit: number = INSTAGRAM_WEEKLY_POST_LIMIT,
): InstagramLimitResult => {
    // Edit mode prefills the platform straight from the stored campaign, so
    // these arrive as unvalidated as the other campaigns' do.
    const perDay = Number.isFinite(postsPerDay) ? postsPerDay : 1;
    const days = Array.isArray(selectedDays) ? selectedDays.length : 0;
    const totalPosts = otherPosts + perDay * days;
    return {
        allowed: totalPosts <= limit,
        otherPosts,
        totalPosts,
        remaining: Math.max(0, limit - otherPosts),
        limit,
    };
};

/** Edit mode feeds occupancy math the OTHER campaigns only; create mode no-ops. */
export const excludeCampaign = (
    settings: CampaignSettingsProps[] | undefined,
    editCampaignId: string | undefined,
): CampaignSettingsProps[] | undefined =>
    settings && editCampaignId
        ? settings.filter((c) => c.campaignId !== editCampaignId)
        : settings;

/**
 * Weekly Instagram posts these campaigns schedule, summed per campaign.
 *
 * Deliberately reads dayOfWeek/postsPerDay rather than the occupied-slot map:
 * slots only exist where postTimes are well formed, and MB-1454 found prod
 * campaigns carrying {hour: null, minute: null}, which would have silently
 * counted those campaigns as zero. Malformed counts fall back to 1 post/day
 * (the Instagram ceiling) instead of poisoning the total with NaN.
 */
export const instagramPostsPerWeek = (
    settings: CampaignSettingsProps[] | undefined,
): number =>
    (settings ?? []).reduce((total, campaign) => {
        const platforms = Array.isArray(campaign?.platforms)
            ? campaign.platforms
            : [];
        return (
            total +
            platforms.reduce((sum, platform) => {
                if (
                    typeof platform?.name !== "string" ||
                    platform.name.toLowerCase() !== "instagram"
                ) {
                    return sum;
                }
                const postsPerDay = Number.isFinite(platform.postsPerDay)
                    ? platform.postsPerDay
                    : 1;
                if (platform.frequency === "Daily") return sum + postsPerDay * 7;
                const days = Array.isArray(platform.dayOfWeek)
                    ? platform.dayOfWeek.length
                    : 0;
                return sum + postsPerDay * days;
            }, 0)
        );
    }, 0);

/**
 * Shared wording for both gates - the day picker and the Next button.
 *
 * Branches on what the user can actually do about it: once other campaigns
 * hold the whole limit, trimming days here can never help.
 */
export const instagramLimitMessage = ({
    limit,
    otherPosts,
    remaining,
}: InstagramLimitResult): string => {
    if (remaining === 0) {
        return `Your other campaigns already use all ${limit} of your weekly Instagram posts. Free one up on another campaign, or remove Instagram from this campaign to continue.`;
    }
    if (otherPosts > 0) {
        return `Instagram is limited to ${limit} posts per week across all your campaigns to protect your account. Your other campaigns already use ${otherPosts}, so you can schedule up to ${remaining} here.`;
    }
    return `Instagram is limited to ${limit} posts per week across all your campaigns to protect your account. Remove a day to continue.`;
};
